import React, { useState } from 'react';

import { Paper, Stack, Typography } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistRuleCard from '../../reportCards/ChecklistRuleCard';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import type { ManualOverrideState } from '../../types';

import PolicyBibleReferenceDialog from './PolicyBibleReferenceDialog';
import type { CampaignRowView } from './types';

const POLICY_BIBLE_ROW_FIELDS = new Set<CampaignRowView['field']>([
  'Date change allowed',
  'Days prior to check-in for date changes',
  'Number of date changes',
]);

interface Props {
  expandedRows: Record<string, boolean>;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<CampaignRowView>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function CampaignReportCard({
  expandedRows,
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const [policyBibleDialogRow, setPolicyBibleDialogRow] = useState<CampaignRowView | null>(null);

  return (
    <ReportCard bordered={false}>
      <Stack spacing={1.5} sx={{ pb: 2 }}>
        {rows.length === 0 ? (
          <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
            <Typography variant="body2">No Campaign rows match this view.</Typography>
          </Paper>
        ) : (
          rows.map((row) => {
            const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
            const approved = isRowApproved(row, manualOverrides);
            const isExpanded = expandedRows[row.id] ?? true;
            const isPolicyBibleRow = POLICY_BIBLE_ROW_FIELDS.has(row.field);

            return (
              <ChecklistRuleCard
                key={row.id}
                approved={approved}
                approvalNote={row.approvalNote}
                dataPoints={row.dataPoints}
                expanded={isExpanded}
                manuallyApproved={manuallyApproved}
                rule={row.rule}
                summary={row.summary}
                title={row.packageName ? `${row.field}: ${row.packageName}` : row.field}
                onApprovalChange={() => onApprovalChange(row.id)}
                onUnapprovalChange={() => onUnapprovalChange(row.id)}
                onToggleExpanded={() => onToggleDetails(row.id, true)}
                onViewPolicyBible={isPolicyBibleRow ? () => setPolicyBibleDialogRow(row) : undefined}
              />
            );
          })
        )}
      </Stack>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="Campaign row" showAutoApproved={showAutoApproved} />
      <PolicyBibleReferenceDialog
        match={policyBibleDialogRow?.policyBibleMatch}
        open={!!policyBibleDialogRow}
        onClose={() => setPolicyBibleDialogRow(null)}
      />
    </ReportCard>
  );
}
